import utilities from './Services/Utilities';
import { IUserContext } from './IUserContext';
import { IUserInfo } from './Utilities/IUserInfo';
import { IWebService } from './Utilities/IWebService';
import { IClaim } from './Utilities/IClaim';
import { SecurityConstants } from './SecurityConstants';

class UserContext implements IUserContext {
    constructor(contextData?: string) {
        var ctx = this.getContext(contextData);
        if (ctx && ctx !== null) {
            this._id = ctx.id;
            this._instance = ctx.instance;
            this._app = ctx.app;
            this._sys = ctx.sys;
            this._auth = ctx.auth;
            this._did = ctx.did;
            this._user = ctx.user ? ctx.user as IUserInfo : null;

            this._claims = this.getClaimsFromJson(ctx.claims);
            this._settings = this.createMap<string>(ctx.settings);
            this._services = this.createMap<IWebService>(ctx.services);
        } else {
            this._id = utilities.data.newGuid();
            this._instance = '';
            this._app = '';
            this._sys = 'default';
            this._auth = '';
            this._did = '';
            this._user = null;
            this._settings = new Map();
            this._claims = [];
            this._services = new Map();
        }
        this.assignDefaultSettings();
    }

    private getContext(contextData: string | undefined): any {
        try {
            if (contextData && contextData !== null) {
                let json = atob(contextData);
                return JSON.parse(json);
            }
            console.log('Invalid context data provided.');
            return null;
        } catch (err) {
            console.log('Invalid context data provided.', err);
            return null;
        }
    }

    private getClaimsFromJson(obj: any): IClaim[] {
        if (obj && obj !== null) {
            if (Array.isArray(obj)) {
                let claims = [] as IClaim[];
                let arr = obj as Array<any>;
                for (let i = 0; i < arr.length; i++) {
                    let o = arr[i];
                    let c = this.createClaim(o);
                    if (c && c !== null) {
                        claims.push(c);
                    }
                }
                return claims;
            }
        }
        return [];
    }

    private createClaim(obj: any): IClaim {
        return {
            type: obj.type,
            val: obj.val,
            props: this.createMap<string>(obj.props)
        };
    }

    private createMap<T>(obj: any): Map<string, T> {
        var map = new Map();
        if (Array.isArray(obj)) {
            let arr = obj as Array<any>;
            for (let i = 0; i < arr.length; i++) {
                map.set(arr[i].key as string, arr[i].value as T);
            }
        } else {
            for (let k of Object.keys(obj)) {
                map.set(k, obj[k] as T);
            }
        }
        return map;
    }

    private _id: string;
    private _instance: string;
    private _app: string;
    private _sys: string;
    private _auth: string;
    private _did: string;
    private _user: IUserInfo | null;

    private _settings: Map<string, string>;
    private _claims: IClaim[];

    private _services: Map<string, IWebService>;
    private _serviceHostTransform?: (s: string, p: string) => string;

    get id(): string {
        return this._id;
    }

    get instance(): string {
        return this._instance;
    }

    get app(): string {
        return this._app;
    }

    get sys(): string {
        return this._sys;
    }

    get auth(): string {
        return this._auth;
    }

    get did(): string {
        return this._did;
    }

    get user(): IUserInfo | null {
        return this._user;
    }

    get settings(): Map<string, string> {
        return this._settings;
    }

    get claims(): IClaim[] {
        return this._claims;
    }

    get services(): Map<string, IWebService> {
        return this._services;
    }

    private assignDefaultSettings(): void {
        if (!this._settings.has('timezone')) {
            this._settings.set('timezone', 'America/Chicago');
        }
        if (!this._settings.has('measureunit')) {
            this._settings.set('measureunit', 'Imperial');
        }
    }

    setServiceHostTransform(transform?: (service: string, path: string) => string): void {
        this._serviceHostTransform = transform;
    }

    authenticate(user: IUserInfo | null, claims: IClaim[] | null, settings: Map<string, string> | null): void {
        this._user = user;
        this._claims = claims ?? [];
        this._settings = settings ?? new Map();
        this.assignDefaultSettings();
    }

    getSetting(name: string): string | null {
        if (this.settings.has(name)) {
            return this.settings.get(name) ?? null;
        }
        return null;
    }

    getClaimValue(type: string): string | null {
        var claim = this.getClaim(type);
        if (claim !== null) {
            return claim.val;
        }
        return null;
    }

    getClaims(type: string): IClaim[] {
        let res = [];
        for (var i = 0; i < this.claims.length; i++) {
            var claim = this.claims[i];
            if (claim.type === type) {
                res.push(claim);
            }
        }
        return res;
    }

    getClaim(type: string, val?: string): IClaim | null {
        for (var i = 0; i < this.claims.length; i++) {
            var claim = this.claims[i];
            if (claim.type === type) {
                if (!val) {
                    return claim;
                }
                if (val && claim.val == val) {
                    return claim;
                }
            }
        }
        return null;
    }

    private cleanPath(path: string): string {
        if (path.startsWith('/')) {
            return path.substring(1);
        }
        return path;
    }

    private getUrl(host: string, path: string) {
        let p = this.cleanPath(path);

        let h = host;
        if (!h.endsWith('/')) {
            h = h + '/';
        }

        return h + p;
    }

    httpUrl(path: string): string {
        return this.getServiceUrl('main', path, true);
    }

    httpsUrl(path: string): string {
        return this.getServiceUrl('main', path);
    }

    getServiceUrl(service: string, path: string, useAlt?: boolean | undefined): string {
        let s = service;
        if (this.services.has(s)) {
            if (useAlt) {
                s = this.services.get(s)?.alt ?? s;
            } else {
                s = this.services.get(s)?.url ?? s;
            }
        }

        if (this._serviceHostTransform) {
            s = this._serviceHostTransform(s, path);
        }

        return this.getUrl(s, path);
    }

    hasService(service: string): boolean {
        return this.services.has(service);
    }

    isAuthenticated(): boolean {
        if (this.user && this.user !== null) {
            return true;
        }
        return false;
    }

    isInRole(role: string): boolean {
        return this.hasClaim(SecurityConstants.Claims.Roles, role);
    }

    hasClaim(claim: string, value?: string): boolean {
        let obj = this.getClaim(claim, value);
        if (obj == null) {
            return false;
        } else {
            return true;
        }
    }
}

export const UserContextFactory = (contextData?: string) => {
    return new UserContext(contextData) as IUserContext;
};